import type { AppContext } from '@application/context/AppContext';
import { TenantProvisioningService } from '@application/licence/TenantProvisioningService';
import { TenantLifecycleService } from '@application/licence/TenantLifecycleService';
import { isCabinetLicenseKey } from '@application/licence/LicenseKeyType';
import { emitActiveTenantChanged } from '@application/licence/ActiveTenant';
import { LicenceStatusService } from '@infra/licence/LicenceStatusService';
import { LicensingSessionService } from '@infra/licence/LicensingSessionService';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { persistLicenceBinding } from '@application/licence/ActivationInstallGuard';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { configureLocalTableStoreForCloud } from '@infra/persistence/LocalTableStore';
import { logger } from '@infra/telemetry/Logger';
import type { Tenant } from '@shared/types/Tenant';
import type { Session, User } from '@shared/types/User';
import { getTenantProfile } from '@application/persistence/TenantProfileService';
import { resolveLicenseKeyType } from '@application/licence/LicenseKeyType';
import type { ActivationClaims } from '@infra/licence/ActivationClaims';
import {
  roleForTenant,
  roleForTenantFromGrants,
  sessionGrantsFromUser,
  userHasTenantAccess,
} from '@shared/access/tenantUserRole';

export interface PortfolioTenantSummary {
  id: string;
  nom: string;
  activite?: string;
  ville?: string;
  active: boolean;
  effectif?: number;
  licensed: boolean;
  /** MD-01 — dossier archivé (données conservées, ouverture refusée). */
  archived?: boolean;
}

/**
 * Portefeuille Multi-entités / Cabinet.
 * Switch sécurisé : JWT du tenant → claims → repos → session Cloud.
 */
export interface ActiveTenantSnapshot {
  tenantId: string | null;
  jwt: string | null;
  binding: string | null;
}

export class CabinetPortfolioService {
  static isCabinetMode(ctx: AppContext): boolean {
    const claims = ctx.getLicenceRuntime()?.getState()?.claims;
    if (!claims) return false;
    // Portefeuille = claim signé (orthogonal au key_type commercial C4).
    if (claims.cabinet === true && typeof claims.cabinet_group_id === 'string') {
      return true;
    }
    return isCabinetLicenseKey(claims);
  }

  /** Cabinet signé OU plusieurs activations locales sur le poste. */
  static async isMultiDossierMode(ctx: AppContext): Promise<boolean> {
    if (CabinetPortfolioService.isCabinetMode(ctx)) return true;
    const ids = await CabinetPortfolioService.listPortfolioTenantIds();
    return ids.length > 1;
  }

  static async listPortfolioTenantIds(): Promise<string[]> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    return Object.keys(portfolio);
  }

  /**
   * Cloud : la fiche utilisateur peut manquer en local — l’admin n’est pas bloqué.
   * Cabinet : l’admin du portefeuille ouvre tous les dossiers déjà dans le JWT.
   */
  static sessionCanOpenTenant(
    session: Session | null | undefined,
    user: User | null | undefined,
    tenantId: string,
    _cabinetMode: boolean,
    remoteMode = false,
  ): boolean {
    // P0-6 : l'accès à une autre entreprise est toujours un octroi explicite, même pour un admin.
    if (user) return CabinetPortfolioService.userCanAccessTenant(user, tenantId);
    if (!session) return true;
    // Poste SQLite : la fiche n'est lisible que depuis son dossier d'origine ; la session porte alors ses droits.
    if (session.grants) return roleForTenantFromGrants(session.grants, tenantId) !== null;
    if (session.tenant_id === tenantId) return true;
    // Cloud : la fiche utilisateur vit côté serveur ; l'authentification du dossier cible
    // (nouveau jeton) reste imposée par l'API.
    return remoteMode && (session.role === 'admin' || session.role === 'super_admin');
  }

  /** Un utilisateur cabinet peut-il ouvrir ce dossier ? (MD-02 : rôle par dossier). */
  static userCanAccessTenant(user: User | null | undefined, tenantId: string): boolean {
    if (!user) return false;
    return userHasTenantAccess(user, tenantId);
  }

  /** MD-02 — rôle effectif pour le dossier cible. */
  static roleForTenant(user: User, tenantId: string) {
    return roleForTenant(user, tenantId);
  }

  static async listSummaries(
    ctx: AppContext,
    opts: { includeArchived?: boolean } = {},
  ): Promise<PortfolioTenantSummary[]> {
    const ids = await CabinetPortfolioService.listPortfolioTenantIds();
    const activeId =
      ctx.tenantContext.tryGetTenant()?.id ??
      ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id ??
      null;
    const session = ctx.auth.getSession();
    const user = session ? await ctx.userRepo.findById(session.user_id).catch(() => null) : null;

    const out: PortfolioTenantSummary[] = [];
    for (const id of ids) {
      if (user && !CabinetPortfolioService.userCanAccessTenant(user, id)) continue;
      if (!user && session?.grants && roleForTenantFromGrants(session.grants, id) === null) continue;
      const profile = await getTenantProfile(ctx, id);
      const archived = TenantLifecycleService.isArchived(profile);
      if (archived && !opts.includeArchived) continue;
      let effectif = 0;
      try {
        effectif = await ctx.localTables.countByTenant('salaries', id);
      } catch {
        effectif = 0;
      }
      const live = id === activeId ? ctx.tenantContext.tryGetTenant() : null;
      out.push({
        id,
        nom:
          live?.raison_sociale ||
          live?.nom ||
          profile?.raison_sociale ||
          profile?.nom ||
          id,
        activite: live?.secteur_activite || profile?.secteur_activite,
        ville: live?.ville || profile?.ville,
        active: id === activeId,
        effectif,
        licensed: true,
        archived,
      });
    }
    return out;
  }

  /**
   * Au démarrage du poste : si la dernière entreprise ouverte est une entreprise cliente du portefeuille, on rouvre le
   * dossier du cabinet (clé Cabinet). C'est là que se trouvent les comptes du cabinet : sans cela, le poste
   * proposerait de créer un « premier administrateur » dans l'entreprise cliente. Retourne le dossier rouvert, ou
   * null si rien ne change.
   */
  static async reopenCabinetHome(): Promise<string | null> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    const activeId = await SecureLicenceStore.getActiveTenantId();
    if (!activeId || Object.keys(portfolio).length < 2) return null;
    const typeOf = (jwt: string | undefined) => {
      if (!jwt) return 'client' as const;
      try {
        const payload = jwt.split('.')[1] ?? '';
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
        return resolveLicenseKeyType(JSON.parse(json) as ActivationClaims);
      } catch {
        return 'client' as const;
      }
    };
    if (typeOf(portfolio[activeId]) === 'cabinet') return null;
    const home = Object.keys(portfolio).find((id) => typeOf(portfolio[id]) === 'cabinet');
    if (!home) return null;
    await SecureLicenceStore.switchActiveCertificate(home);
    return home;
  }

  /** Photographie l'entreprise active du coffre de licences (pour y revenir après une activation). */
  static async captureActiveTenant(): Promise<ActiveTenantSnapshot> {
    return {
      tenantId: await SecureLicenceStore.getActiveTenantId(),
      jwt: await SecureLicenceStore.getActivationCertificate(),
      binding: await SecureLicenceStore.getLicenceBinding(),
    };
  }

  /** Rétablit l'entreprise active photographiée (licence, état de licence, données). */
  static async restoreActiveTenant(ctx: AppContext, snapshot: ActiveTenantSnapshot): Promise<void> {
    const runtime = ctx.getLicenceRuntime();
    if (!snapshot.tenantId || !snapshot.jwt || !runtime) return;
    try {
      await SecureLicenceStore.restoreActiveCertificate(
        snapshot.tenantId,
        snapshot.jwt,
        snapshot.binding,
      );
      await runtime.loadFromStore(snapshot.tenantId, 0);
      await LicenceStatusService.refreshForActiveTenant(runtime);
      await ctx.rebindPersistenceForTenant();
    } catch (err) {
      logger.error({
        event: 'cabinet_switch_rollback_failed',
        err: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Bascule vers une autre entreprise du portefeuille.
   * Standalone : rebind Dexie/SQLite. Cloud : nouvelle session installation.
   */
  static async switchToTenant(
    ctx: AppContext,
    tenantId: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    const portfolioIds = await CabinetPortfolioService.listPortfolioTenantIds();
    if (!CabinetPortfolioService.isCabinetMode(ctx) && portfolioIds.length < 2) {
      return { ok: false, error: 'Le mode Multi-dossiers n’est pas activé sur cette licence.' };
    }
    if (!portfolioIds.includes(tenantId)) {
      return {
        ok: false,
        error: 'Aucune licence activée pour cette entreprise. Activez d’abord sa clé commerciale.',
      };
    }

    const profile = await getTenantProfile(ctx, tenantId);
    if (TenantLifecycleService.isArchived(profile)) {
      return {
        ok: false,
        error:
          'Ce dossier est archivé. Réactivez-le depuis le portefeuille avant de l’ouvrir.',
      };
    }

    const session = ctx.auth.getSession();
    if (session) {
      const user = await ctx.userRepo.findById(session.user_id).catch(() => null);
      // Fiche lisible (on est sur le dossier d'origine) : les droits portés par la session sont remis à jour,
      // car ils serviront une fois un autre dossier ouvert (fiche alors illisible) et après un redémarrage.
      if (user) ctx.auth.adoptSessionGrants(sessionGrantsFromUser(user));
      if (
        !CabinetPortfolioService.sessionCanOpenTenant(
          session,
          user,
          tenantId,
          CabinetPortfolioService.isCabinetMode(ctx),
          isRemoteMultiStationMode(ctx.getLicenceRuntime()?.getState()?.claims?.data_mode ?? null),
        )
      ) {
        return { ok: false, error: 'Vous n’avez pas accès à cette entreprise cliente.' };
      }
    }

    const previousTenantId = await SecureLicenceStore.getActiveTenantId();
    const previousJwt = await SecureLicenceStore.getActivationCertificate();
    const previousBinding = await SecureLicenceStore.getLicenceBinding();
    const runtime = ctx.getLicenceRuntime();
    if (!runtime) {
      return { ok: false, error: 'Runtime licence indisponible.' };
    }

    const rollback = async (): Promise<void> => {
      await CabinetPortfolioService.restoreActiveTenant(ctx, {
        tenantId: previousTenantId,
        jwt: previousJwt,
        binding: previousBinding,
      });
    };

    const jwt = await SecureLicenceStore.switchActiveCertificate(tenantId);
    if (!jwt) {
      return {
        ok: false,
        error: 'Aucune licence activée pour cette entreprise. Activez d’abord sa clé commerciale.',
      };
    }

    const state = await runtime.loadFromStore(tenantId, 0);
    if (!state.valid || !state.claims) {
      await rollback();
      return { ok: false, error: 'Certificat invalide pour cette entreprise.' };
    }
    if (state.claims.tenant_id !== tenantId) {
      await rollback();
      return { ok: false, error: 'Incohérence tenant / certificat.' };
    }
    if (CabinetPortfolioService.isCabinetMode(ctx)) {
      if (!state.claims.cabinet || !state.claims.cabinet_group_id) {
        await rollback();
        return { ok: false, error: 'Ce dossier n’est pas une licence Cabinet.' };
      }
    }

    let currentGroup: string | undefined;
    if (previousBinding) {
      try {
        currentGroup = (JSON.parse(previousBinding) as { cabinet_group_id?: string })
          .cabinet_group_id;
      } catch {
        currentGroup = undefined;
      }
    }
    await persistLicenceBinding(state.claims);
    // P0-3 : restriction (révocation/suspension/absence de contrôle) propre à l'entreprise ouverte.
    await LicenceStatusService.refreshForActiveTenant(runtime);

    // Cloisonnement immédiat : toute lecture locale / cache vise le nouveau dossier
    // avant même la session Cloud (évite le mélange avec l’entreprise précédente).
    ctx.tenantContext.clear();
    configureLocalTableStoreForCloud(
      isRemoteMultiStationMode(state.claims.data_mode),
      tenantId,
    );

    if (isRemoteMultiStationMode(state.claims.data_mode)) {
      await SecureLicenceStore.clearInstallationSession();
      const ok = await LicensingSessionService.ensureInstallationSession(tenantId);
      if (!ok) {
        await rollback();
        return {
          ok: false,
          error: 'Session Cloud impossible pour cette entreprise. Vérifiez la connexion.',
        };
      }
    }

    try {
      await ctx.rebindPersistenceForTenant();
    } catch (err) {
      logger.error({
        event: 'cabinet_switch_rebind_failed',
        err: err instanceof Error ? err.message : String(err),
      });
      await rollback();
      return { ok: false, error: 'Impossible de recharger les données de l’entreprise.' };
    }

    await TenantProvisioningService.ensureTenantFromLicence(ctx);

    if (session) {
      await ctx.auth.adoptSessionTenant(tenantId);
    }

    emitActiveTenantChanged(tenantId);

    logger.info({
      event: 'cabinet_tenant_switched',
      tenantId,
      cabinetGroupId: state.claims.cabinet_group_id,
      previousGroup: currentGroup,
    });

    return { ok: true };
  }

  /** Liste locale des tenants (profils) même hors portefeuille licence — admin only diagnostic. */
  static async listLocalTenantProfiles(ctx: AppContext): Promise<Tenant[]> {
    return ctx.localTables.list<Tenant>('tenants');
  }
}
